import {
  Accordion,
  Alert,
  AlertIcon,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
  List,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectableCard } from "../../../../../common/input/SelectableCard";

export type ProcedureShelf = "common" | "repeated" | "exceptions";

export type ProcedureAvailability = "choosable" | "comingSoon" | { barredBecause: string };

export interface ProcedureEntry {
  key: string;
  shelf: ProcedureShelf;
  name: string;
  description: string;
  legalBasis?: string | null;
  isSelected: boolean;
  availability: ProcedureAvailability;
  onSelect: () => void;
}

interface Props {
  entries: ProcedureEntry[];
  savedProcedureAlreadyWarned?: boolean;
  savedProcedureRefused?: { name: string } | null;
}

export const ProcedureShelves = ({ entries, savedProcedureAlreadyWarned = false, savedProcedureRefused = null }: Props) => {
  const { t } = useTranslation();
  const choosable = entries.filter((entry) => entry.availability === "choosable");
  const onShelf = (shelf: ProcedureShelf) => choosable.filter((entry) => entry.shelf === shelf);
  const exceptions = onShelf("exceptions");
  const comingSoon = entries.filter((entry) => entry.availability === "comingSoon");
  const barred = entries.flatMap((entry) =>
    typeof entry.availability === "object" ? [{ ...entry, reason: entry.availability.barredBecause }] : [],
  );
  const savedButBarred = barred.find((entry) => entry.isSelected);
  const exceptionChosen = exceptions.some((entry) => entry.isSelected);
  const [exceptionsOpen, setExceptionsOpen] = useState(exceptionChosen);
  const [exceptionWasChosen, setExceptionWasChosen] = useState(exceptionChosen);
  if (exceptionChosen !== exceptionWasChosen) {
    setExceptionWasChosen(exceptionChosen);
    if (exceptionChosen) setExceptionsOpen(true);
  }

  return (
    <Stack spacing="6" data-testid="procedure-shelves">
      {savedProcedureRefused && !savedButBarred && !savedProcedureAlreadyWarned && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {t("{{procedure}} can't be used here. Choose the procedure again.", { procedure: savedProcedureRefused.name })}
        </Alert>
      )}
      {savedButBarred && !savedProcedureAlreadyWarned && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {t("{{procedure}} can't be used here: {{reason}}. Choose the procedure again.", {
            procedure: savedButBarred.name,
            reason: savedButBarred.reason,
          })}
        </Alert>
      )}
      {onShelf("common").length > 0 && (
        <Shelf heading={t("Common procedures")} entries={onShelf("common")} />
      )}
      {onShelf("repeated").length > 0 && (
        <Shelf
          heading={t("Schemes for repeated purchases")}
          help={t("Announced once. Afterwards you run competitions among the suppliers in the scheme.") ?? undefined}
          entries={onShelf("repeated")}
        />
      )}
      {exceptions.length > 0 && (
        <Accordion allowToggle index={exceptionsOpen ? 0 : -1} onChange={(index) => setExceptionsOpen(index === 0)}>
          <AccordionItem border="1px solid" borderColor="smBorder" borderRadius="lg">
            <AccordionButton py="3" borderRadius="lg">
              <Heading as="h3" size="sm" flex="1" textAlign="left">
                {t("Exceptions and special competitions ({{count}})", { count: exceptions.length })}
              </Heading>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb="4">
              <Cards entries={exceptions} />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      {comingSoon.length > 0 && (
        <Text fontSize="sm" color="smMuted">
          {t("Coming soon: {{procedures}}.", { procedures: comingSoon.map((entry) => entry.name).join(", ") })}
        </Text>
      )}
      {barred.length > 0 && (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton px="0" py="2">
              <Text fontSize="sm" color="smPrimary" flex="1" textAlign="left">
                {t("Why don't I see all the procedures? ({{count}})", { count: barred.length })}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px="0" pb="2">
              <List spacing="2" fontSize="sm" color="smMuted">
                {barred.map((entry) => (
                  <ListItem key={entry.key}>
                    {entry.name}: {entry.reason}
                  </ListItem>
                ))}
              </List>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Stack>
  );
};

const Shelf = ({ heading, help, entries }: { heading: string; help?: string; entries: ProcedureEntry[] }) => (
  <Box>
    <Heading as="h3" size="sm" mb={help ? "1" : "3"}>
      {heading}
    </Heading>
    {help && (
      <Text fontSize="sm" color="smMuted" mb="3">
        {help}
      </Text>
    )}
    <Cards entries={entries} />
  </Box>
);

const Cards = ({ entries }: { entries: ProcedureEntry[] }) => (
  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
    {entries.map((entry) => (
      <SelectableCard key={entry.key} isSelected={entry.isSelected} onClick={entry.onSelect}>
        <Text fontWeight="semibold" fontSize="sm" color="smText">
          {entry.name}
        </Text>
        <Text fontSize="xs" color="smMuted" mt="1">
          {entry.description}
        </Text>
        {entry.isSelected && entry.legalBasis && (
          <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
            {entry.legalBasis}
          </Text>
        )}
      </SelectableCard>
    ))}
  </SimpleGrid>
);
